/**
 * Marketing-app client. The dashboard server proxies these calls to
 * `MARKETING_APP_URL` over the private VPC, so the browser only ever
 * speaks to the dashboard host. That keeps every agent box (marketing,
 * sales, gateway) off the public internet.
 */
import { api } from "../../lib/api.js";
import type {
  AgentName,
  AgentSettings,
  AgentSettingsUpdate,
  AgentStatusRow,
  AgentThought,
  Campaign,
  Capabilities,
  ComposeRequest,
  Draft,
  DraftReviewAction,
  Economics,
  Idea,
  KnowledgeHealth,
  KnowledgeSource,
  MarketingMetricsOverview,
  PlatformAccount,
  PressReleaseTrigger,
  PublishAttempt,
  ScheduledPost,
  Trend,
} from "./types.js";

const PREFIX = "/api/marketing";

export const marketingApi = {
  metricsOverview: () =>
    api<MarketingMetricsOverview>(`${PREFIX}/metrics/overview`),

  // Trends
  listTrends: (status: string = "active") =>
    api<Trend[]>(
      `${PREFIX}/trends?status=${encodeURIComponent(status)}&limit=200`,
    ),
  suppressTrend: (id: number) =>
    api<Trend>(`${PREFIX}/trends/${id}/suppress`, { method: "POST" }),

  // Drafts
  listDrafts: (params: { status?: string; platform?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api<Draft[]>(`${PREFIX}/drafts${suffix}`);
  },
  getDraft: (id: number) => api<Draft>(`${PREFIX}/drafts/${id}`),
  reviewDraft: (id: number, body: DraftReviewAction) =>
    api<Draft>(`${PREFIX}/drafts/${id}/review`, { method: "POST", body }),
  regenerateDraft: (id: number, hints: Record<string, unknown> = {}) =>
    api<Draft>(`${PREFIX}/drafts/${id}/regenerate`, {
      method: "POST",
      body: { hints },
    }),
  // Hard delete — permanent. Gated server-side on marketing:edit.
  deleteDraft: (id: number) =>
    api<{ deleted: boolean; id: number }>(`${PREFIX}/drafts/${id}`, {
      method: "DELETE",
    }),
  bulkDeleteDrafts: (ids: number[]) =>
    api<{ deleted_ids: number[]; not_found_ids: number[] }>(
      `${PREFIX}/drafts/bulk-delete`,
      { method: "POST", body: { ids } },
    ),

  // Create New — free-text brief → one grounded, branded draft in review.
  compose: (body: ComposeRequest) =>
    api<Draft>(`${PREFIX}/compose`, { method: "POST", body }),

  // Ideas — AI-suggested topics the operator can act on.
  listIdeas: (limit = 60) =>
    api<Idea[]>(`${PREFIX}/ideas?limit=${limit}`),
  generateIdea: (trendId: number, platform: string) =>
    api<Draft>(`${PREFIX}/ideas/${trendId}/generate`, {
      method: "POST",
      body: { platform },
    }),
  dismissIdea: (trendId: number) =>
    api<{ dismissed: boolean; id: number }>(
      `${PREFIX}/ideas/${trendId}/dismiss`,
      { method: "POST" },
    ),
  // Multi-select companion to `dismissIdea` — suppress many ideas in one call.
  bulkDismissIdeas: (ids: number[]) =>
    api<{ dismissed_ids: number[]; not_found_ids: number[] }>(
      `${PREFIX}/ideas/bulk-dismiss`,
      { method: "POST", body: { ids } },
    ),
  // Manager-driven brainstorm: a free-text brief → a few topic ideas added to
  // the pool (operator-requested, not counted against the daily idea quota).
  brainstormIdeas: (brief: string, count = 4) =>
    api<Idea[]>(`${PREFIX}/ideas/brainstorm`, {
      method: "POST",
      body: { brief, count },
    }),

  // Budget / "what it knows" — mirrors the sales agent's economics + brain.
  getEconomics: () => api<Economics>(`${PREFIX}/economics`),
  getCapabilities: () => api<Capabilities>(`${PREFIX}/capabilities`),

  // Knowledge-base readability probe (the KB is PDF-backed; confirm the
  // agent can actually read it right now).
  knowledgeHealth: () => api<KnowledgeHealth>(`${PREFIX}/knowledge/health`),
  probeKnowledge: (query: string) =>
    api<KnowledgeHealth>(`${PREFIX}/knowledge/probe`, {
      method: "POST",
      body: { query },
    }),

  // Review queue. Pull the WHOLE pending backlog (the API returns a
  // lightweight, image-free list, so a high limit is cheap) — the table
  // paginates 25/page client-side and bulk-delete works across every page.
  // A capped window over a large backlog was the "deletes keep coming back"
  // illusion: deleting a visible row just slid the next one into view.
  reviewQueue: (limit = 500) =>
    api<Draft[]>(`${PREFIX}/reviews/queue?limit=${limit}`),

  // Campaigns
  listCampaigns: () => api<Campaign[]>(`${PREFIX}/campaigns`),
  createCampaign: (body: Pick<Campaign, "name"> & Partial<Campaign>) =>
    api<Campaign>(`${PREFIX}/campaigns`, { method: "POST", body }),
  setCampaignStatus: (id: number, status: string) =>
    api<Campaign>(
      `${PREFIX}/campaigns/${id}?status=${encodeURIComponent(status)}`,
      { method: "PATCH" },
    ),

  // Agent control
  agentStatus: () => api<AgentStatusRow[]>(`${PREFIX}/agent/status`),
  runAgent: (name: AgentName) =>
    api<{ agent: AgentName; status: string; summary: unknown }>(
      `${PREFIX}/agent/run/${name}`,
      { method: "POST", body: {} },
    ),

  // Agent settings (paused / steer / autonomy) — drives the Workbench banner.
  getAgentSettings: () => api<AgentSettings>(`${PREFIX}/agent/settings`),
  patchAgentSettings: (body: AgentSettingsUpdate) =>
    api<AgentSettings>(`${PREFIX}/agent/settings`, {
      method: "PATCH",
      body,
    }),

  // Agent thoughts (reasoning trace)
  listThoughts: (
    params: {
      agent_name?: string;
      thought_type?: string;
      draft_id?: number;
      trend_id?: number;
      press_trigger_id?: number;
      run_id?: number;
      since_minutes?: number;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api<AgentThought[]>(`${PREFIX}/thoughts${suffix}`);
  },
  liveThoughts: (after_id: number | null = null, limit = 50) => {
    const qs = new URLSearchParams();
    if (after_id !== null) qs.set("after_id", String(after_id));
    qs.set("limit", String(limit));
    return api<AgentThought[]>(`${PREFIX}/thoughts/live?${qs}`);
  },

  // Knowledge sources
  listKnowledge: (params: { kind?: string; status?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api<KnowledgeSource[]>(`${PREFIX}/knowledge${suffix}`);
  },
  archiveKnowledge: (id: number) =>
    api<KnowledgeSource>(`${PREFIX}/knowledge/${id}/archive`, { method: "POST" }),

  // Schedule
  listScheduled: (
    params: {
      status?: string;
      platform?: string;
      draft_id?: number;
      from?: string;
      to?: string;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api<ScheduledPost[]>(`${PREFIX}/schedule${suffix}`);
  },
  scheduleDraft: (body: { draft_id: number; scheduled_for: string; notes?: string }) =>
    api<ScheduledPost>(`${PREFIX}/schedule`, { method: "POST", body }),
  reschedulePost: (id: number, body: { scheduled_for: string; notes?: string }) =>
    api<ScheduledPost>(`${PREFIX}/schedule/${id}`, { method: "PATCH", body }),
  cancelPost: (id: number) =>
    api<ScheduledPost>(`${PREFIX}/schedule/${id}/cancel`, {
      method: "POST",
      body: {},
    }),
  listAttempts: (id: number) =>
    api<PublishAttempt[]>(`${PREFIX}/schedule/${id}/attempts`),

  // Press release triggers
  listPressTriggers: (status: string = "detected") =>
    api<PressReleaseTrigger[]>(
      `${PREFIX}/press-triggers?status=${encodeURIComponent(status)}&limit=200`,
    ),
  actOnPressTrigger: (
    id: number,
    body: { action: "draft_now" | "dismiss"; notes?: string },
  ) =>
    api<PressReleaseTrigger>(`${PREFIX}/press-triggers/${id}/action`, {
      method: "POST",
      body,
    }),

  // Platform accounts
  listPlatformAccounts: () => api<PlatformAccount[]>(`${PREFIX}/platforms`),
  registerPlatformAccount: (body: {
    platform: string;
    account_name: string;
    display_name?: string;
    credentials_ref?: string;
  }) =>
    api<PlatformAccount>(`${PREFIX}/platforms`, { method: "POST", body }),
  disconnectPlatformAccount: (id: number) =>
    api<PlatformAccount>(`${PREFIX}/platforms/${id}/disconnect`, {
      method: "POST",
    }),
  // Connect (upsert account + set encrypted token in one call). `token` is
  // request-only — it is never present on any response.
  connectPlatform: (body: {
    platform: string;
    account_name: string;
    display_name?: string;
    token: string;
  }) => api<PlatformAccount>(`${PREFIX}/platforms/connect`, { method: "POST", body }),
  setPlatformCredentials: (id: number, token: string) =>
    api<PlatformAccount>(`${PREFIX}/platforms/${id}/credentials`, {
      method: "PUT",
      body: { token },
    }),
  testPlatform: (id: number) =>
    api<PlatformAccount>(`${PREFIX}/platforms/${id}/test`, { method: "POST" }),
  setPlatformPublish: (id: number, publish_enabled: boolean) =>
    api<PlatformAccount>(`${PREFIX}/platforms/${id}`, {
      method: "PATCH",
      body: { publish_enabled },
    }),
  // Master autonomous-publishing switch (runtime; lives on agent settings).
  setPublisherEnabled: (publisher_enabled: boolean) =>
    api<AgentSettings>(`${PREFIX}/agent/settings`, {
      method: "PATCH",
      body: { publisher_enabled },
    }),
};
